import {
  Button,
  FormCheck,
  FormControl,
  FormGroup,
  FormLabel,
  FormSelect,
  FormText,
  InputGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'react-bootstrap';
import { useContext, useEffect, useState } from 'react';
import marketSignalApi from '@/modules/market-signals/api/market-signal-api';
import { MarketSignalAlarmConfig } from '@/modules/market-signals/model/market-signals/market-signal-alarm-config';
import {
  MarketSignalContext,
  marketSignalSounds,
} from '@/modules/market-signals/contexts/market-signal-context';
import FormRange from 'react-bootstrap/FormRange';
import { Credentials, authService } from '@/core/auth/export';
import { permissionToGroupName } from '@/modules/market-signals/components/market-signals/alarms/permissions-to-group-name';

const defaultForm: MarketSignalAlarmConfig = {
  type: 'llm',
  condition: '',
  temperature: 0.1,
  soundType: marketSignalSounds[0].file,
  streams: [],
  enabled: true,
};

export default function MarketSignalAlarmEditModal({
  alarm,
  onClose,
}: {
  alarm?: MarketSignalAlarmConfig;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState<MarketSignalAlarmConfig>(defaultForm);
  const [credentials, setCredentials] = useState<Credentials>();
  const { streams } = useContext(MarketSignalContext);

  useEffect(() => {
    authService.onTokenAvailable().then(value => {
      setCredentials(authService.getCredentials());
    });
  }, []);

  useEffect(() => {
    if (alarm) setFormData({ ...alarm });
    else setFormData(defaultForm);
  }, [alarm]);

  const setType = (s: string) => {
    if (!formData) return;

    formData.type = s as 'llm' | 'string';
    setFormData({ ...formData });
  };

  const setSoundType = (s: string) => {
    if (!formData) return;

    formData.soundType = s;
    setFormData({ ...formData });
  };

  const setOwner = (s: string) => {
    if (!formData) return;

    if (!s || s === '') {
      formData.owner = undefined;
    } else {
      formData.owner = {
        type: 'group',
        ownerId: s,
      };
    }

    console.log('Set owner', formData.owner);

    setFormData({ ...formData });
  };

  const setCondition = (s: string) => {
    if (!formData) return;

    formData.condition = s;
    setFormData({ ...formData });
  };

  const setTemperature = (s: string) => {
    if (!formData) return;

    if (formData.type !== 'llm') return;

    formData.temperature = parseFloat(s);
    setFormData({ ...formData });
  };

  const setMatchOR = (s: boolean) => {
    if (!formData) return;

    if (formData.type !== 'string') return;

    formData.doOrSeparator = s;
    setFormData({ ...formData });
  };

  const setShowDesktopNotif = (s: boolean) => {
    if (!formData) return;

    formData.showDesktopNotif = s;
    setFormData({ ...formData });
  };

  const setIncludeStream = (s: string, include: boolean) => {
    if (!formData) return;

    if (!formData.streams) formData.streams = [];

    if (include) {
      if (!formData.streams.includes(s)) formData.streams.push(s);
    } else {
      formData.streams = formData.streams.filter(value => value !== s);
    }

    setFormData({ ...formData });
  };

  const save = () => {
    if (formData) {
      if (formData._id) marketSignalApi.saveAlarmConfig(formData._id, formData).then(onClose);
      else marketSignalApi.createAlarmConfig(formData).then(onClose);
    }
  };

  const GroupOptions = ({ permissions }: { permissions?: string[] }) => {
    if (!permissions) return <></>;

    return (
      <>
        {permissions.map(value => (
          <option key={value} value={value}>
            Group: [{permissionToGroupName(value)}]
          </option>
        ))}
      </>
    );
  };

  let playing = false;
  const playSound = async (s: string) => {
    if (playing) return;

    if (s.trim() === '') return;

    console.log('Playing sound:', s);

    const audio = new Audio(`/sounds/${s}.mp3`);

    try {
      playing = true;
      await audio.play();
    } catch (e) {
      console.error('Error playing sound');
    } finally {
      playing = false;
      console.log('Playing sound finished:', s);
    }
  };

  if (!formData) return <></>;

  return (
    <Modal show={!!alarm} onBackdropClick={onClose} onHide={onClose}>
      <ModalHeader>Edit Alarm</ModalHeader>

      <ModalBody>
        <FormGroup className={'mb-2'}>
          <FormLabel>Type</FormLabel>
          <FormSelect value={formData.type} onChange={event => setType(event.target.value)}>
            <option value={'llm'}>Natural Language</option>
            <option value={'string'}>Text Search</option>
          </FormSelect>
          {formData.type === 'llm' && (
            <FormText>
              <p className={'mt-1'}>
                A <strong>Natural Language</strong> alarm will process the text using an LLM, and
                trigger if the condition above is matched. "Loose" or "infered" conditions can be
                used, for example:
              </p>
              <p>
                <em>
                  Send an alarm if{' '}
                  <b>it is suggested that new investments will be made in solar energy</b>
                </em>
              </p>
            </FormText>
          )}
          {formData.type === 'string' && (
            <FormText>
              <p className={'mt-1'}>
                A <strong>Text Search</strong> alarm will search the text of incoming items and to
                an exact text match against the given terms.
              </p>
            </FormText>
          )}
        </FormGroup>
        <FormGroup className={'mb-2'}>
          <FormLabel>
            {formData.type === 'llm' && 'Send an alarm if:'}
            {formData.type === 'string' && 'Search the text for:'}
          </FormLabel>
          <FormControl
            type={'text'}
            value={formData.condition}
            onChange={event => setCondition(event.target.value)}
          ></FormControl>
          {formData.type === 'string' && <FormText>Enter * to search for everything</FormText>}
        </FormGroup>

        {formData.type === 'llm' && (
          <FormGroup className={'mb-2'}>
            <FormLabel>Temperature: {formData.temperature}</FormLabel>
            <div>
              <FormRange
                value={formData.temperature}
                min={0}
                max={2}
                step={0.1}
                onChange={event => setTemperature(event.target.value)}
              ></FormRange>
            </div>
            <FormText>
              The <b>Temperature</b> controls the 'signal to noise' ratio. Lower means better
              instruction-following (signal), but more rigid thought process. Higher means better
              association skills and more creativity, but increases the chance for hallucination
              (noise)
            </FormText>
          </FormGroup>
        )}

        {formData.type === 'string' && (
          <FormGroup className={'mb-2'}>
            <FormLabel>Match Type</FormLabel>
            <FormCheck
              checked={formData.doOrSeparator}
              type={'switch'}
              label={formData.doOrSeparator ? 'Match ANY' : 'Match ALL'}
              onChange={event => setMatchOR(event.target.checked)}
            ></FormCheck>
            <FormText>
              {formData.doOrSeparator &&
                'If the text contains ANY of the terms above, an alert will be generated'}
              {!formData.doOrSeparator &&
                'If the text contains ALL of the terms above, in order, an alert will be generated'}
            </FormText>
          </FormGroup>
        )}

        <FormGroup className={'mb-2'}>
          <FormLabel>Desktop Alerts</FormLabel>
          <FormCheck
            checked={formData.showDesktopNotif}
            type={'switch'}
            onChange={event => setShowDesktopNotif(event.target.checked)}
          ></FormCheck>
          <FormText>Will only display if website notifications are allowed</FormText>
        </FormGroup>

        <FormGroup className={'mb-2'}>
          <FormLabel>Alarm Sound</FormLabel>
          <InputGroup>
            <FormSelect
              value={formData.soundType || marketSignalSounds[0].file}
              onChange={event => setSoundType(event.target.value)}
            >
              {marketSignalSounds.map(value => (
                <option key={`sound-${value.name}`} value={value.file}>
                  {value.name}
                </option>
              ))}
            </FormSelect>

            <Button variant={'secondary'} onClick={() => playSound(formData.soundType)}>
              Preview
            </Button>
          </InputGroup>
        </FormGroup>

        <FormGroup className={'mb-2'}>
          <FormLabel>Audience</FormLabel>
          <FormSelect
            value={formData.owner?.ownerId || ''}
            onChange={event => setOwner(event.target.value)}
          >
            <option key={''} value={''}>
              Only Me
            </option>
            <GroupOptions
              permissions={credentials?.body.permissions.filter(p => /member:group:.*/.test(p))}
            />
          </FormSelect>
        </FormGroup>
        <FormGroup className={'mb-2'}>
          <FormLabel>
            Streams{' '}
            {(!formData.streams || formData.streams.length === 0) && '(Will Include All Streams)'}
          </FormLabel>
          {streams.map(value => (
            <FormCheck
              label={value.name}
              onChange={event => setIncludeStream(value.code, event.target.checked)}
              checked={formData.streams?.includes(value.code)}
            ></FormCheck>
          ))}
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button variant={'warning'} onClick={onClose}>
          Cancel
        </Button>
        <Button variant={'success'} onClick={save}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}
